// Facet navigation -- pushState, fetch the filtered page, swap results in place -- SG 20261019
// The flyout stays mounted: only the grid, bottom pagination, bar count, badge, chips,
// footer and option state are replaced, so focus and scroll position survive each change.

export const FACETS_RENDERED_EVENT = "facets:rendered";

let activeRequest: AbortController | null = null;
let popstateBound = false;

export function resolveFacetUrl(rawUrl: string | null | undefined): URL | null {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl, window.location.origin);
    return url.origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
}

// Builds the next URL from the live checkbox state rather than the server's per-value
// add/remove URLs, so a second tick made before the first response lands is not lost.
// Size options carry data-facet-in-stock: in-stock limiting stays only while a size is ticked,
// because availability has no control of its own to clear it -- SG 20261019
export function buildFilterUrl(root: ParentNode): URL {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const inputs = Array.from(root.querySelectorAll<HTMLInputElement>('[data-facets-groups] input[type="checkbox"]'));
  const names = new Set(inputs.map((input) => input.name));

  names.forEach((name) => params.delete(name));
  inputs.filter((input) => input.checked).forEach((input) => params.append(input.name, input.value));

  const inStockInputs = inputs.filter((input) => input.hasAttribute("data-facet-in-stock"));
  if (inStockInputs.some((input) => input.checked)) {
    if (!params.has("filter.v.availability")) params.set("filter.v.availability", "1");
  } else if (inStockInputs.length) {
    params.delete("filter.v.availability");
  }

  params.delete("page");
  url.hash = "";
  return url;
}

export function navigateFacets(rawUrl: string | URL | null | undefined, push: boolean = true) {
  const url = rawUrl instanceof URL ? rawUrl : resolveFacetUrl(rawUrl);
  if (!url || url.origin !== window.location.origin) return;

  const target = `${url.pathname}${url.search}`;
  if (push) history.pushState({ facets: true }, "", target);

  activeRequest?.abort();
  const request = new AbortController();
  activeRequest = request;
  setLoading(true);

  fetch(target, { signal: request.signal, credentials: "same-origin" })
    .then((response) => {
      const type = response.headers.get("content-type") || "";
      if (!response.ok || type.indexOf("text/html") === -1) return null;
      return response.text();
    })
    .then((html) => {
      if (request !== activeRequest || html === null) return;
      renderFacetResults(new DOMParser().parseFromString(html, "text/html"));
    })
    .catch((error: Error) => {
      if (error.name !== "AbortError") console.error(error);
    })
    .finally(() => {
      if (request === activeRequest) {
        activeRequest = null;
        setLoading(false);
      }
    });
}

export function bindFacetPopstate() {
  if (popstateBound) return;
  popstateBound = true;
  window.addEventListener("popstate", () => {
    // A zero-result search swaps the whole collection-main and removes the bar, so key off the main element -- EZ 20261028
    if (!document.querySelector("collection-main")) return;
    navigateFacets(`${window.location.pathname}${window.location.search}`, false);
  });
}

function setLoading(isLoading: boolean) {
  const main = document.querySelector("collection-main");
  main?.classList.toggle("loading", isLoading);
  document.querySelector("collection-grid")?.setAttribute("aria-busy", isLoading ? "true" : "false");
}

function renderFacetResults(doc: Document) {
  const currentMain = document.querySelector("collection-main");
  const nextMain = doc.querySelector("collection-main");
  if (!currentMain || !nextMain) return;

  const currentFacets = currentMain.querySelector("collection-facets");
  const nextFacets = nextMain.querySelector("collection-facets");
  const currentGrid = currentMain.querySelector("collection-grid");
  const nextGrid = nextMain.querySelector("collection-grid");

  // Search with zero results renders no bar or grid: fall back to a full section swap.
  // The swap removes the count live region, so focus the no-results message to announce it -- SG 20261027
  if (!currentFacets || !nextFacets || !currentGrid || !nextGrid) {
    document.dispatchEvent(new CustomEvent("facets:close"));
    const replacement = document.importNode(nextMain, true) as Element;
    currentMain.replaceWith(replacement);
    const message = replacement.querySelector<HTMLElement>(".search__no-result");
    if (message) {
      message.setAttribute("tabindex", "-1");
      message.focus();
    }
    return;
  }

  const focusRestore = captureFocus(currentFacets);

  replaceChildren(currentGrid, nextGrid);
  swapPagination(currentMain, nextMain, currentGrid);

  ["[data-facets-badge]", "[data-facets-chips]", ".collection__facets-clear"].forEach((selector) => {
    const current = currentFacets.querySelector(selector);
    const next = nextFacets.querySelector(selector);
    if (current && next) current.replaceWith(document.importNode(next, true));
  });

  ["[data-facets-count]", "[data-facets-show]"].forEach((selector) => {
    const current = currentFacets.querySelector(selector);
    const next = nextFacets.querySelector(selector);
    if (current && next && current.textContent !== next.textContent) current.textContent = next.textContent;
  });

  syncGroups(currentFacets, nextFacets);
  syncSort(currentFacets, nextFacets);
  focusRestore();

  currentFacets.dispatchEvent(new CustomEvent(FACETS_RENDERED_EVENT, { bubbles: true }));
}

function replaceChildren(current: Element, next: Element) {
  while (current.firstChild) current.removeChild(current.firstChild);
  Array.from(next.childNodes).forEach((node) => current.appendChild(document.importNode(node, true)));
}

function swapPagination(currentMain: Element, nextMain: Element, grid: Element) {
  const current = currentMain.querySelector("collection-pagination.bottom");
  const next = nextMain.querySelector("collection-pagination.bottom");
  if (current && next) current.replaceWith(document.importNode(next, true));
  else if (current) current.remove();
  else if (next) grid.insertAdjacentElement("afterend", document.importNode(next, true));
}

function groupSignature(root: Element) {
  return Array.from(root.querySelectorAll<HTMLInputElement>("[data-facets-groups] input"))
    .map((input) => `${input.id}|${input.name}|${input.value}`)
    .join(",");
}

// Same groups and values: update state in place so the focused checkbox keeps focus.
// Different groups (a filter became active and un-hid): swap the list, keep open groups.
function syncGroups(currentFacets: Element, nextFacets: Element) {
  const currentGroups = currentFacets.querySelector("[data-facets-groups]");
  const nextGroups = nextFacets.querySelector("[data-facets-groups]");
  if (!currentGroups || !nextGroups) return;

  if (groupSignature(currentFacets) !== groupSignature(nextFacets)) {
    const openParams = Array.from(currentGroups.querySelectorAll<HTMLElement>(".collection__facet.is-open")).map(
      (group) => group.dataset.param
    );
    const replacement = document.importNode(nextGroups, true) as Element;
    replacement.querySelectorAll<HTMLElement>(".collection__facet").forEach((group) => {
      if (openParams.indexOf(group.dataset.param) !== -1) setGroupOpen(group, true);
    });
    currentGroups.replaceWith(replacement);
    return;
  }

  nextGroups.querySelectorAll<HTMLInputElement>("input").forEach((nextInput) => {
    const input = currentGroups.querySelector<HTMLInputElement>(`#${CSS.escape(nextInput.id)}`);
    if (!input) return;
    input.checked = nextInput.checked;
    input.disabled = nextInput.disabled;
    ["data-add-url", "data-remove-url"].forEach((attr) => {
      const value = nextInput.getAttribute(attr);
      if (value !== null) input.setAttribute(attr, value);
    });
    input.closest(".collection__facet-option")?.classList.toggle("checked", nextInput.checked);

    const count = input.closest("label")?.querySelector("[data-facet-count]");
    const nextCount = nextInput.closest("label")?.querySelector("[data-facet-count]");
    if (count && nextCount && count.textContent !== nextCount.textContent) count.textContent = nextCount.textContent;
  });
}

function syncSort(currentFacets: Element, nextFacets: Element) {
  nextFacets.querySelectorAll<HTMLInputElement>('input[name="sort_by"]').forEach((nextInput) => {
    const input = currentFacets.querySelector<HTMLInputElement>(`#${CSS.escape(nextInput.id)}`);
    if (input) input.checked = nextInput.checked;
  });
}

export function setGroupOpen(group: Element, open: boolean) {
  group.classList.toggle("is-open", open);
  group.querySelector("[data-facet-toggle]")?.setAttribute("aria-expanded", open ? "true" : "false");
}

// A removed chip takes focus with it: move to the next chip, then the first group, then the trigger.
function captureFocus(facets: Element) {
  const active = document.activeElement as HTMLElement | null;
  if (!active || !facets.contains(active)) return () => {};
  if (!active.classList.contains("collection__facets-chip")) return captureControlFocus(facets, active);

  const chips = Array.from(facets.querySelectorAll(".collection__facets-chip"));
  const index = chips.indexOf(active);

  return () => {
    const nextChips = Array.from(facets.querySelectorAll<HTMLElement>(".collection__facets-chip"));
    const target =
      nextChips[index] ||
      nextChips[index - 1] ||
      facets.querySelector<HTMLElement>("[data-facet-toggle]") ||
      facets.querySelector<HTMLElement>(".collection__facets-drawer .close");
    target?.focus();
  };
}

// A swapped group list or clear button takes focus with it: return to the same control by id,
// then its group heading, then the flyout's close button, so focus stays in the open flyout -- SG 20261020
function captureControlFocus(facets: Element, active: HTMLElement) {
  const id = active.id;
  const param = active.closest<HTMLElement>(".collection__facet")?.dataset.param;

  return () => {
    // A control that was just disabled still reads as active, but the browser drops focus to body next -- EZ 20261028
    const current = document.activeElement as HTMLInputElement | null;
    if (current && facets.contains(current) && !current.disabled) return;
    const byId = id ? facets.querySelector<HTMLInputElement>(`#${CSS.escape(id)}`) : null;
    const target =
      (byId && !byId.disabled ? byId : null) ||
      (param ? facets.querySelector<HTMLElement>(`.collection__facet[data-param="${CSS.escape(param)}"] [data-facet-toggle]`) : null) ||
      facets.querySelector<HTMLElement>(".collection__facets-drawer .close");
    target?.focus({ preventScroll: true });
  };
}
